import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraDataTable, SpecraSectionHeader } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { MaskedField } from '../../../lib/access/MaskedField';
import { programRef } from '../../../lib/access/types';
import { ErrorState } from '../_shared/components/PageStates';
import { formatMoney, orderCeilingLabel } from '../_shared/lib/recordLabels';
import { useProgramOrderCeilings } from './hooks';
// The document a value comes from, as the reviewer reads it.
const documentLabel = (sourceRef)=>sourceRef === 'award' ? 'Award' : sourceRef;
/**
 * Order ceilings: the ceilings the whole order carries (T&M NTE, travel NTE),
 * not one line. Each kind is a group with every document's value, oldest
 * first; the last is the one in force.
 */ export function OrderCeilings({ programId }) {
    const ref = programRef(programId);
    const { data, isLoading, isError } = useProgramOrderCeilings(programId);
    if (isError) {
        return /*#__PURE__*/ _jsx(ErrorState, {
            title: "Couldn't load order ceilings"
        });
    }
    const rows = data ?? [];
    const byKind = new Map();
    for (const c of rows){
        byKind.set(c.kind, [
            ...byKind.get(c.kind) ?? [],
            c
        ]);
    }
    const currentIds = new Set([
        ...byKind.values()
    ].map((history)=>history[history.length - 1].id));
    const money = (c)=>/*#__PURE__*/ _jsx(MaskedField, {
            fieldName: "ceiling_amount",
            resource: ref,
            children: formatMoney(c.ceilingAmount)
        });
    const groups = [
        ...byKind.entries()
    ].map(([kind, history])=>({
            id: kind,
            label: orderCeilingLabel(kind),
            rows: history,
            summary: money(history[history.length - 1])
        }));
    const columns = [
        {
            id: 'document',
            header: 'Document',
            cell: (c)=>documentLabel(c.sourceRef)
        },
        {
            id: 'effective',
            header: 'Takes effect',
            cell: (c)=>c.effectiveDate || '—'
        },
        {
            id: 'state',
            header: 'State',
            cell: (c)=>currentIds.has(c.id) ? /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "info",
                    children: "Current"
                }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "neutral",
                    children: "Superseded"
                })
        },
        {
            id: 'amount',
            header: 'Ceiling',
            align: 'right',
            cell: money
        }
    ];
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "data-testid": Testids.deliveryFinancial.orderCeilings,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Order ceilings"
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                columns: columns,
                groups: groups,
                getRowId: (c)=>c.id,
                loading: isLoading,
                rowTestId: Testids.deliveryFinancial.orderCeilingRow,
                getRowProps: (c)=>({
                        'data-kind': c.kind,
                        'data-source': c.sourceRef,
                        'data-current': currentIds.has(c.id) ? 'true' : 'false'
                    }),
                emptyTitle: "No order ceilings on file",
                emptyDescription: "The award and its modifications state no ceiling for the whole order, or they have not been read yet."
            })
        ]
    });
}
